import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import apiClient from '../services/apiClient';
import { AuthContext } from './AuthContext';
import {
    DASHBOARD_SCOPE_STORAGE_KEY,
    DEFAULT_DASHBOARD_PROJECT_ID,
    DEFAULT_DASHBOARD_PROJECT_STATUS,
    DEFAULT_DASHBOARD_TIME_RANGE,
    buildDashboardScopeApiParams,
    loadStoredDashboardScope,
    resolveDashboardDateRange,
    saveDashboardScope,
    scopeToTimelinePreset,
    timelinePresetToScope,
} from '../utils/dashboardScope';
import { reportingPeriodModeToDashboardTimeRange } from '../utils/reportingPeriodMode';

const DashboardScopeContext = createContext(null);

// Fallback when `AuthContext` isn't a valid Context object (e.g. a test's
// jest.mock('.../AuthContext', ...) factory omits the AuthContext export).
// useContext(undefined) throws; useContext(NULL_AUTH_CONTEXT) just yields
// null, matching the existing "outside AuthProvider" tolerance below.
const NULL_AUTH_CONTEXT = createContext(null);

/**
 * Shared timeline + portfolio filters for all PSA dashboard routes.
 */
export const DashboardScopeProvider = ({ children }) => {
    const auth = useContext(AuthContext || NULL_AUTH_CONTEXT);
    // Outside AuthProvider (unit tests): skip org fetch. Inside: wait for session.
    const authLoading = !auth || Boolean(auth.loading);
    const token = auth?.token ?? null;
    const stored = loadStoredDashboardScope();
    const [timeRange, setTimeRangeState] = useState(stored.timeRange);
    const [projectId, setProjectIdState] = useState(stored.projectId);
    const [projectStatus, setProjectStatusState] = useState(stored.projectStatus);
    const orgDefaultAppliedRef = useRef(false);

    const persist = useCallback((nextScope) => {
        saveDashboardScope({
            timeRange: nextScope.timeRange,
            projectId: nextScope.projectId,
            projectStatus: nextScope.projectStatus,
        });
    }, []);

    const setTimeRange = useCallback((nextTimeRange) => {
        const normalized = nextTimeRange || DEFAULT_DASHBOARD_TIME_RANGE;
        setTimeRangeState(normalized);
        setProjectIdState((currentProjectId) => {
            setProjectStatusState((currentProjectStatus) => {
                persist({
                    timeRange: normalized,
                    projectId: currentProjectId,
                    projectStatus: currentProjectStatus,
                });
                return currentProjectStatus;
            });
            return currentProjectId;
        });
    }, [persist]);

    // Org settings require auth. Wait until the session is ready so cold start /
    // login never fires an unauthenticated /psa-org-settings/ request (401 overlay).
    useEffect(() => {
        if (authLoading || !token || typeof window === 'undefined') {
            return undefined;
        }
        if (orgDefaultAppliedRef.current) {
            return undefined;
        }

        if (window.sessionStorage.getItem(DASHBOARD_SCOPE_STORAGE_KEY)) {
            orgDefaultAppliedRef.current = true;
            return undefined;
        }

        orgDefaultAppliedRef.current = true;
        let cancelled = false;

        const applyOrgDefault = async () => {
            try {
                const response = await apiClient.get('/psa-org-settings/');
                const nextTimeRange = reportingPeriodModeToDashboardTimeRange(
                    response.data?.default_reporting_period_mode
                );
                if (cancelled || !nextTimeRange) {
                    return;
                }
                setTimeRangeState(nextTimeRange);
                persist({
                    timeRange: nextTimeRange,
                    projectId: stored.projectId,
                    projectStatus: stored.projectStatus,
                });
            } catch {
                // Keep built-in default when org settings are unavailable.
            }
        };

        applyOrgDefault();

        return () => {
            cancelled = true;
        };
    }, [authLoading, token, persist, stored.projectId, stored.projectStatus]);

    const setProjectId = useCallback((nextProjectId) => {
        const normalized = nextProjectId != null ? `${nextProjectId}` : DEFAULT_DASHBOARD_PROJECT_ID;
        setProjectIdState(normalized);
        setTimeRangeState((currentTimeRange) => {
            setProjectStatusState((currentProjectStatus) => {
                persist({
                    timeRange: currentTimeRange,
                    projectId: normalized,
                    projectStatus: currentProjectStatus,
                });
                return currentProjectStatus;
            });
            return currentTimeRange;
        });
    }, [persist]);

    const setProjectStatus = useCallback((nextProjectStatus) => {
        const normalized = nextProjectStatus || DEFAULT_DASHBOARD_PROJECT_STATUS;
        setProjectStatusState(normalized);
        setTimeRangeState((currentTimeRange) => {
            setProjectIdState((currentProjectId) => {
                persist({
                    timeRange: currentTimeRange,
                    projectId: currentProjectId,
                    projectStatus: normalized,
                });
                return currentProjectId;
            });
            return currentTimeRange;
        });
    }, [persist]);

    const clearPortfolioFilters = useCallback(() => {
        setProjectIdState(DEFAULT_DASHBOARD_PROJECT_ID);
        setProjectStatusState(DEFAULT_DASHBOARD_PROJECT_STATUS);
        setTimeRangeState((currentTimeRange) => {
            persist({
                timeRange: currentTimeRange,
                projectId: DEFAULT_DASHBOARD_PROJECT_ID,
                projectStatus: DEFAULT_DASHBOARD_PROJECT_STATUS,
            });
            return currentTimeRange;
        });
    }, [persist]);

    const setTimelinePreset = useCallback((preset) => {
        setTimeRange(timelinePresetToScope(preset));
    }, [setTimeRange]);

    const dateRange = useMemo(
        () => resolveDashboardDateRange(timeRange),
        [timeRange]
    );

    const timelinePreset = useMemo(
        () => scopeToTimelinePreset(timeRange),
        [timeRange]
    );

    const portfolioApiParams = useMemo(
        () => buildDashboardScopeApiParams({ dateRange, projectId, projectStatus }),
        [dateRange, projectId, projectStatus]
    );

    const value = useMemo(() => ({
        timeRange,
        setTimeRange,
        timelinePreset,
        setTimelinePreset,
        projectId,
        setProjectId,
        projectStatus,
        setProjectStatus,
        clearPortfolioFilters,
        dateRange,
        portfolioApiParams,
    }), [
        timeRange,
        setTimeRange,
        timelinePreset,
        setTimelinePreset,
        projectId,
        setProjectId,
        projectStatus,
        setProjectStatus,
        clearPortfolioFilters,
        dateRange,
        portfolioApiParams,
    ]);

    return (
        <DashboardScopeContext.Provider value={value}>
            {children}
        </DashboardScopeContext.Provider>
    );
};

/**
 * Access shared dashboard timeline/project scope.
 *
 * @returns {{
 *   timeRange: string,
 *   setTimeRange: Function,
 *   timelinePreset: string,
 *   setTimelinePreset: Function,
 *   projectId: string,
 *   setProjectId: Function,
 *   projectStatus: string,
 *   setProjectStatus: Function,
 *   clearPortfolioFilters: Function,
 *   dateRange: { start_date: string, end_date: string },
 *   portfolioApiParams: Record<string, string>
 * }}
 */
export const useDashboardScope = () => {
    const context = useContext(DashboardScopeContext);
    if (!context) {
        throw new Error('useDashboardScope must be used within DashboardScopeProvider');
    }
    return context;
};
